import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import type {
  MyPermissions,
  RolePermissions,
  UserRole,
  ResourceName,
  PermissionAction
} from '../types/permissions';
import { getMyPermissions, hasPermission as checkPermission } from '../services/permissionsApi';
import { socketService } from '../services/socket';

interface PermissionsContextType {
  // State
  myRole: UserRole | null;
  myPermissions: RolePermissions | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  refreshPermissions: () => Promise<void>;
  hasPermission: (resource: ResourceName, action: PermissionAction) => boolean;
  canManage: (resource: ResourceName) => boolean;
  canView: (resource: ResourceName) => boolean;
  isOwnerOrAdmin: () => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
  projectId?: string; // Optional: can be provided directly or will use URL params
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children, projectId: providedProjectId }) => {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const projectId = providedProjectId || urlProjectId;

  const [myRole, setMyRole] = useState<UserRole | null>(null);
  const [myPermissions, setMyPermissions] = useState<RolePermissions | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load permissions from API
   */
  const loadPermissions = useCallback(async () => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const data: MyPermissions = await getMyPermissions(projectId);
      setMyRole(data.role);
      setMyPermissions(data.permissions);
    } catch (err: any) {
      console.error('Error loading permissions:', err);
      setError(err.response?.data?.message || 'Erro ao carregar permissões');

      // Set null values on error
      setMyRole(null);
      setMyPermissions(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  /**
   * Refresh permissions (public API for manual refresh)
   */
  const refreshPermissions = useCallback(async () => {
    await loadPermissions();
  }, [loadPermissions]);

  /**
   * Check if user has specific permission
   */
  const hasPermission = useCallback((resource: ResourceName, action: PermissionAction): boolean => {
    return checkPermission(myPermissions, resource, action);
  }, [myPermissions]);

  /**
   * Check if user can manage a resource
   */
  const canManage = useCallback((resource: ResourceName): boolean => {
    return hasPermission(resource, 'manage');
  }, [hasPermission]);

  /**
   * Check if user can view a resource
   */
  const canView = useCallback((resource: ResourceName): boolean => {
    return hasPermission(resource, 'view');
  }, [hasPermission]);

  /**
   * Check if user is owner or admin
   */
  const isOwnerOrAdmin = useCallback((): boolean => {
    return myRole === 'owner' || myRole === 'admin';
  }, [myRole]);

  /**
   * Load permissions on mount and project change
   */
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  /**
   * Socket.io listener for real-time permission updates
   */
  useEffect(() => {
    if (!projectId) return;

    const handlePermissionsUpdated = (data: any) => {
      console.log('Permissions updated via socket:', data);

      // Reload permissions when they change
      if (data.projectId === projectId) {
        refreshPermissions();
      }
    };

    // Listen for permission updates
    socketService.on('permissions:updated', handlePermissionsUpdated);

    // Cleanup listener on unmount
    return () => {
      socketService.off('permissions:updated', handlePermissionsUpdated);
    };
  }, [projectId, refreshPermissions]);

  const value: PermissionsContextType = {
    myRole,
    myPermissions,
    isLoading,
    error,
    refreshPermissions,
    hasPermission,
    canManage,
    canView,
    isOwnerOrAdmin
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Custom hook to access permissions context
 *
 * @example
 * const { myRole, canManage, canView } = usePermissions();
 *
 * if (canManage('tasks')) {
 *   // Show create/edit/delete buttons
 * }
 *
 * if (canView('hoursBank')) {
 *   // Show hours bank tab
 * }
 */
export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);

  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }

  return context;
};

/**
 * HOC to wrap components that need permissions
 *
 * @example
 * export default withPermissions(TasksPage);
 */
export const withPermissions = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => (
    <PermissionsProvider>
      <Component {...props} />
    </PermissionsProvider>
  );
};
